import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  GraduationCap,
  KeyRound,
  PartyPopper,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { OnboardingTerms } from '../components/hiring/OnboardingTerms';
import type { CandidateStatusResponse } from '../components/hiring';

export const Route = createFileRoute('/_authed/candidate/onboard')({
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const statusQuery = useQuery({
    queryKey: ['me', 'hiring', 'status'] as const,
    queryFn: ({ signal }) =>
      apiRequest<CandidateStatusResponse>('/api/me/hiring/status', { signal }),
  });

  const acceptMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>('/api/me/hiring/onboard/accept', {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      toast('Welcome to Babul Ilm! Set your password to finish.', {
        variant: 'success',
      });
      void navigate({ to: '/change-password' });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not finish onboarding.',
        { variant: 'error' },
      ),
  });

  const stage = statusQuery.data?.application.pipelineStage;
  const ready = stage === 'rules_passed';
  const alreadyOnboarded = stage === 'onboarded';

  return (
    <ListShell
      title="Onboarding"
      description="One final step — accept the offer and set your password."
      primaryAction={
        <Link
          to="/candidate/status"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to my application
        </Link>
      }
    >
      <GatedSection
        error={statusQuery.error}
        title="Onboarding"
        onRetry={() => statusQuery.refetch()}
      >
        {statusQuery.isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : alreadyOnboarded ? (
          <OnboardedView />
        ) : ready ? (
          <AcceptView
            onAccept={() => acceptMutation.mutate()}
            pending={acceptMutation.isPending}
          />
        ) : (
          <NotReadyView />
        )}
      </GatedSection>
    </ListShell>
  );
}

function AcceptView({
  onAccept,
  pending,
}: {
  onAccept: () => void;
  pending: boolean;
}) {
  return (
    <section className="mx-auto max-w-2xl space-y-6 rounded-2xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-6 dark:from-gold-700/20">
      <div className="flex items-start gap-3">
        <span className="rounded-full bg-gold-100 p-2 text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
          <PartyPopper size={22} aria-hidden="true" />
        </span>
        <div className="space-y-1">
          <h2 className="font-display text-2xl font-semibold">
            Welcome to Babul Ilm Academy
          </h2>
          <p className="text-sm text-[color:var(--color-text-muted)]">
            You've completed every step. By accepting onboarding your account
            is promoted to a teacher account.
          </p>
        </div>
      </div>
      <OnboardingTerms heading="Your offer" />
      <ol className="space-y-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4 text-sm">
        <li className="flex items-start gap-2">
          <CheckCircle2
            size={16}
            className="mt-0.5 text-[color:var(--color-success)]"
            aria-hidden="true"
          />
          <span>Your teacher record will be created automatically.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2
            size={16}
            className="mt-0.5 text-[color:var(--color-success)]"
            aria-hidden="true"
          />
          <span>You'll be moved to the teacher portal on your next login.</span>
        </li>
        <li className="flex items-start gap-2">
          <KeyRound
            size={16}
            className="mt-0.5 text-[color:var(--color-text-muted)]"
            aria-hidden="true"
          />
          <span>
            Right after accepting, you'll set a new password to secure the
            account.
          </span>
        </li>
      </ol>
      <div className="flex justify-end">
        <Button
          size="lg"
          icon={<CheckCircle2 size={18} aria-hidden="true" />}
          loading={pending}
          onClick={onAccept}
        >
          Accept &amp; set my password
        </Button>
      </div>
    </section>
  );
}

function OnboardedView() {
  return (
    <section className="mx-auto max-w-2xl space-y-4 rounded-2xl border border-[color:var(--color-success)]/30 bg-[color:var(--color-success)]/10 p-6 text-[color:var(--color-text)]">
      <div className="flex items-start gap-3">
        <span className="rounded-full bg-[color:var(--color-success)]/20 p-2 text-[color:var(--color-success)]">
          <GraduationCap size={20} aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-xl font-semibold">
            You're already onboarded
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            Welcome aboard. Use the link below to head over to your teacher
            dashboard.
          </p>
        </div>
      </div>
      <div className="flex justify-end">
        <Link to="/today">
          <Button>Go to teacher dashboard</Button>
        </Link>
      </div>
    </section>
  );
}

function NotReadyView() {
  return (
    <section className="mx-auto max-w-2xl space-y-2 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-6">
      <h2 className="font-display text-xl font-semibold">
        Not ready to onboard yet
      </h2>
      <p className="text-sm text-[color:var(--color-text-muted)]">
        Complete the rules test first. Once you pass, the Accept button will
        appear here. You can always check your current step on the{' '}
        <Link to="/candidate/status" className="text-gold-700 underline">
          status page
        </Link>
        .
      </p>
    </section>
  );
}
